import {
  extractLastNameComponent,
  extractWorkflowFromInstanceName,
} from '@spotify/confidence-core-react';
import {
  FlagIndexPageQuery,
  ListMetricsQuery,
  ListWorkflowInstancesQuery,
  PulsesQuery,
  WorkflowV1WorkflowInstanceCheckState,
  getTypeOrNull,
} from '@spotify/confidence-graphql';

import type { ResourceType, SearchResultItem } from './types';

export const normalizeFlagResults = (
  data: FlagIndexPageQuery | undefined,
): SearchResultItem[] => {
  if (!data?.flags || data.flags.__typename === 'Error') return [];

  return data.flags.flags.map(flag => ({
    resourceType: 'flag' as const,
    name: flag.name,
    displayName: extractLastNameComponent(flag.name) ?? flag.name,
    state: flag.state,
    ownerName: getTypeOrNull(flag.owner)?.displayName,
    createTime: flag.createTime,
    updateTime: undefined,
  }));
};

export const normalizeMetricResults = (
  data: ListMetricsQuery | undefined,
): SearchResultItem[] => {
  if (!data?.metrics || data.metrics.__typename === 'Error') return [];

  return data.metrics.metrics.map(metric => ({
    resourceType: 'metric' as const,
    name: metric.name,
    displayName:
      metric.displayName ||
      extractLastNameComponent(metric.name) ||
      metric.name,
    state: undefined,
    ownerName: getTypeOrNull(metric.owner)?.displayName,
    createTime: undefined,
    updateTime: undefined,
  }));
};

export const normalizePulseResults = (
  data: PulsesQuery | undefined,
): SearchResultItem[] => {
  if (!data?.pulses || data.pulses.__typename === 'Error') return [];

  return data.pulses.pulses.map(pulse => ({
    resourceType: 'pulse' as const,
    name: pulse.name,
    displayName:
      pulse.displayName || extractLastNameComponent(pulse.name) || pulse.name,
    state: undefined,
    ownerName: getTypeOrNull(pulse.owner)?.displayName,
    createTime: undefined,
    updateTime: pulse.updateTime,
  }));
};

const workflowIdToResourceType = (
  workflowId: string | undefined,
): ResourceType => {
  if (workflowId === 'rollout') return 'rollout';
  if (workflowId === 'analysis' || workflowId === 'reanalysis')
    return 'analysis';
  return 'abtest';
};

export const normalizeExperimentResults = (
  data: ListWorkflowInstancesQuery | undefined,
): SearchResultItem[] => {
  if (!data?.workflowInstances || data.workflowInstances.__typename === 'Error')
    return [];

  return data.workflowInstances.workflowInstances.map(instance => {
    const hasFailedChecks = instance.checks.some(
      check => check.state === WorkflowV1WorkflowInstanceCheckState.Failed,
    );

    return {
      resourceType: workflowIdToResourceType(
        extractWorkflowFromInstanceName(instance.name),
      ),
      name: instance.name,
      displayName:
        instance.displayName ||
        extractLastNameComponent(instance.name) ||
        instance.name,
      state: instance.state,
      ownerName: getTypeOrNull(instance.owner)?.displayName,
      createTime: instance.createTime,
      updateTime: instance.updateTime,
      plannedStartTime: instance.moduleData2?.planning?.planned?.startTime,
      plannedEndTime: instance.moduleData2?.planning?.planned?.endTime,
      workflowState: {
        state: instance.state,
        pendingTransition: !!instance.pendingTransition,
        hasFailedChecks,
      },
    };
  });
};
